import { StationeryProduct } from '@shared/utils/print-price/types';
import { calculateStationeryPricing } from '@shared/utils/print-price/utils/calculatePrices';
import { useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';
import { useShippingMethodLabelsAndDescriptions } from '@apps/card/hooks/useShippingMethodLabelsAndDescriptions';

type ShippingMethodFilters = {
  quantity: number;
  isPOBox: boolean;
  countryCode: string;
};

type ShippingMethodInfo = {
  id: string;
  title: string;
  description?: string;
  price: number;
};

const isNotNull = (input: ShippingMethodInfo | null): input is ShippingMethodInfo => {
  return input !== null;
};

export const useGetValidProductShippingOptions = (product: StationeryProduct, filters: ShippingMethodFilters): ShippingMethodInfo[] => {
  const { tq } = useTranslation('stationery');

  const shippingMethodLabelsAndDescriptions = useShippingMethodLabelsAndDescriptions();

  const processedShippingMethods = (product.shippingOptions ?? [])
    .map(method => {
      if (method.__typename !== 'StationeryProductShippingOptionV1') return null;

      const isValid = method.constraints.every(c => {
        if (c.__typename === 'StationeryAllowPoBoxShippingConstraint') {
          if (!c.allowPoBox && filters.isPOBox) {
            return false;
          }
          return true;
        }
        if (c.__typename === 'StationeryCountryShippingConstraint') {
          const { includedCountries, excludedCountries } = c;
          const currentCountry = filters.countryCode;
          return (includedCountries?.some(country => currentCountry === country) ?? true) && (excludedCountries?.every(country => currentCountry !== country) ?? true);
        }
        if (c.__typename === 'StationeryQuantityShippingConstraint') {
          const { minQuantity, maxQuantity } = c;
          const quantity = filters.quantity;
          return quantity >= minQuantity && quantity <= (maxQuantity ?? quantity);
        }
        return false;
      });

      if (!isValid) return null;

      const price = calculateStationeryPricing(method.pricing, filters.quantity);
      const labelAndDescription = shippingMethodLabelsAndDescriptions[method.id as keyof typeof shippingMethodLabelsAndDescriptions];
      if (!labelAndDescription) addError(`Invalid delivery method id`, { deliveryMethodId: method.id, productId: product.id });

      const { title, description } = labelAndDescription ?? { title: tq`Unknown`, description: undefined };
      return {
        id: method.id,
        title,
        price,
        description,
      };
    })
    .filter(isNotNull) as ShippingMethodInfo[];

  processedShippingMethods.sort((a, b) => a.price - b.price);
  return processedShippingMethods;
};
